import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { TModalsContext } from '@/contexts/ModalsProvider/types';

import { handleError } from '@/utils/errorUtils';

import { GenericModal } from '@/components/modal/GenericModal';
import { GenericModalClose } from '@/components/modal/GenericModalClose';
import { SubmitError } from '@/components/submitError/SubmitError';

import { useUpdateAccountInfo } from '@/mutations/profile';
import { TStationLocation } from '@/pages/Auth/SignUpProfile/types';

import { BusinessAddressForm } from './BusinessAddressForm';
import { styles } from './ChangeBusinessAddress.styles';

type TChangeBusinessAddressProps = TModalsContext & {
  onSuccess: (data: TStationLocation) => void;
  initialValues?: TStationLocation;
};

export const ChangeBusinessAddress = ({
  close,
  initialValues,
  onSuccess,
}: TChangeBusinessAddressProps) => {
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  // Save W13: marks Settings > Account Details old, so it refreshes behind the closing modal.
  const { mutateAsync: saveAccount } = useUpdateAccountInfo();
  const closeModal = () => close('settings.changeBusinessAddress');

  const onSubmit = async (data: TStationLocation) => {
    try {
      await saveAccount(data);
      closeModal();
      onSuccess(data);
    } catch (err) {
      setError(`${t('errors.unknown')}\n${t('errors.tryAgain')}`);
      handleError(err, 'UNHANDLED_RESPONSE');
    }
  };

  return (
    <GenericModal sx={styles.modal}>
      <GenericModalClose onClick={closeModal} />

      <SubmitError
        error={error}
        dismiss={() => setError(null)}
        sx={{ top: 0, padding: 1, marginTop: 4 }}
      />

      <BusinessAddressForm
        initialValues={initialValues}
        handleSubmit={onSubmit}
        closeModal={closeModal}
      />
    </GenericModal>
  );
};
